import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SearchFilter from './SearchFilter';
import {
  fetchDirectoryBreakdown,
  fetchAiBreakdown,
  selectDirectoryBreakdown,
  selectAiBreakdown,
  selectEngagementError,
} from '../../../store/engagementAnalyticsSlice';

const thClass =
  'h-11 px-6 py-4 text-base font-medium text-[#757575] text-center align-middle bg-[#F7F7F7] whitespace-nowrap';
const tdClass =
  'h-16 px-6 py-4 text-base text-[#1D2739] text-center align-middle border-b border-[#EEE] bg-white [tr:last-child_&]:border-b-0';

export default function AdminEngagement() {
  const dispatch = useDispatch();
  const [viewBy, setViewBy] = useState('Department');
  const isTenant = viewBy === 'Tenants';
  const viewByParam = isTenant ? 'tenant' : 'department';
  const [loading, setLoading] = useState(false);

  const directoryRows = useSelector(selectDirectoryBreakdown);
  const aiRows = useSelector(selectAiBreakdown);
  const error = useSelector(selectEngagementError);

  useEffect(() => {
    const params = { activeFilter: 'This Month', viewBy: viewByParam };
    setLoading(true);
    Promise.all([
      dispatch(fetchDirectoryBreakdown(params)).unwrap(),
      dispatch(fetchAiBreakdown(params)).unwrap(),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dispatch, viewByParam]);

  return (
    <div className="p-6 space-y-8">
      <h1 className="text-2xl font-medium text-[#1D2739] text-left">Engagement</h1>
      <SearchFilter showFilterButton={false} showViewBy onViewByChange={setViewBy} />

      {error && (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-600">
          {error}
        </div>
      )}

      <div>
        <h2 className="text-base font-semibold text-[#1D2739] mb-3 text-left">
          Supplier Directory Engagement (Global)
        </h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                {isTenant && <th className={thClass}>Tenant</th>}
                <th className={thClass}>Department</th>
                <th className={thClass}>Supplier profiles viewed</th>
                <th className={thClass}>Searches Performed</th>
                <th className={thClass}>Filters applied</th>
                <th className={thClass}>Matches returned</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isTenant ? 6 : 5} className={`${tdClass} py-8 text-[#7E7E7E]`}>
                    Loading…
                  </td>
                </tr>
              ) : directoryRows.length === 0 ? (
                <tr>
                  <td colSpan={isTenant ? 6 : 5} className={`${tdClass} py-8 text-[#7E7E7E]`}>
                    No directory engagement data for this period
                  </td>
                </tr>
              ) : (
                directoryRows.map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50">
                    {isTenant && <td className={tdClass}>{row.tenant}</td>}
                    <td className={tdClass}>{row.department}</td>
                    <td className={tdClass}>{row.profilesViewed}</td>
                    <td className={tdClass}>{row.searchesPerformed}</td>
                    <td className={tdClass}>{row.filtersApplied}</td>
                    <td className={tdClass}>{row.matchesReturned}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold text-[#1D2739] mb-3 text-left">
          AI Engagement Metrics (Tenant-Scoped, Aggregated)
        </h2>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                {isTenant && <th className={thClass}>Tenant</th>}
                <th className={thClass}>Department</th>
                <th className={thClass}>Matching calls</th>
                <th className={thClass}>Ranking requests</th>
                <th className={thClass}>Average inference latency</th>
                <th className={thClass}>Drift/Bias flag</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isTenant ? 6 : 5} className={`${tdClass} py-8 text-[#7E7E7E]`}>
                    Loading…
                  </td>
                </tr>
              ) : aiRows.length === 0 ? (
                <tr>
                  <td colSpan={isTenant ? 6 : 5} className={`${tdClass} py-8 text-[#7E7E7E]`}>
                    No AI engagement data for this period
                  </td>
                </tr>
              ) : (
                aiRows.map((row, i) => (
                  <tr key={i} className="border-b last:border-b-0 hover:bg-gray-50">
                    {isTenant && <td className={tdClass}>{row.tenant}</td>}
                    <td className={tdClass}>{row.department}</td>
                    <td className={tdClass}>{row.matchingCalls}</td>
                    <td className={tdClass}>{row.rankingRequests}</td>
                    <td className={tdClass}>{row.avgInferenceLatency}</td>
                    <td className={tdClass}>{row.driftBiasFlag}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
